import React from "react";
import { FiArrowRight, FiChevronDown, FiMinus, FiPlus } from "react-icons/fi";
import { formatPrice } from "../../utils/formatPrice";

// Shared building blocks of the company seat and options steps

export const CREATION_STEPS = [
  "Propriétaire",
  "Entreprise",
  "Services",
  "Paiement",
  "Finalisation",
];

export const isUnique = (prices) => prices.unique !== undefined;

export const billingFor = (prices, periodicity) =>
  isUnique(prices)
    ? "lifetime"
    : periodicity === "annuel"
      ? "yearly"
      : "monthly";

export const totalFor = (prices, periodicity) =>
  isUnique(prices)
    ? prices.unique
    : periodicity === "annuel"
      ? prices.annuel * 12
      : prices.mensuel;

export const cartItemFor = (prices, periodicity) =>
  isUnique(prices)
    ? { amount: prices.unique }
    : { periodicity, amount: prices[periodicity] };

export function PeriodPicker({ value, onChange }) {
  return (
    <div className="option-periods" role="radiogroup" aria-label="Formule">
      {["mensuel", "annuel"].map((periodicity) => (
        <button
          key={periodicity}
          type="button"
          role="radio"
          aria-checked={value === periodicity}
          className={value === periodicity ? "is-active" : ""}
          onClick={() => onChange(periodicity)}
        >
          {periodicity === "mensuel" ? "Mensuel" : "Annuel"}
        </button>
      ))}
    </div>
  );
}

export function PriceBlock({ prices, periodicity }) {
  if (isUnique(prices)) {
    return (
      <p className="option-price">
        <strong>{formatPrice(prices.unique)}</strong>
        <span>paiement unique</span>
      </p>
    );
  }

  const yearlySaving = (prices.mensuel - prices.annuel) * 12;

  return (
    <>
      <p className="option-price">
        <strong>{formatPrice(prices[periodicity])}</strong>
        <span>/ mois</span>
        {periodicity === "annuel" && (
          <em>soit {formatPrice(prices.annuel * 12)} par an</em>
        )}
      </p>
      {periodicity === "annuel" && yearlySaving > 0 && (
        <p className="option-saving">
          Vous économisez {formatPrice(yearlySaving)} par an
        </p>
      )}
    </>
  );
}

export function Details({ guide, open, onToggle }) {
  return (
    <>
      <button
        type="button"
        className={`option-details-toggle ${open ? "is-open" : ""}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        {open ? "Masquer le détail" : "Voir le détail"}
        <FiChevronDown aria-hidden="true" />
      </button>
      {open && (
        <ul className="option-details">
          {guide.items.map((item) => (
            <li key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </li>
          ))}
          {guide.note && <li className="option-details-note">{guide.note}</li>}
        </ul>
      )}
    </>
  );
}

// Quantity picker bounded by a minimum and a step
export function QuantityStepper({ value, min, step, unitLabel, onChange }) {
  return (
    <div className="option-stepper" role="group" aria-label="Quantité">
      <button
        type="button"
        aria-label="Diminuer la quantité"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
      >
        <FiMinus aria-hidden="true" />
      </button>
      <output aria-live="polite">
        <strong>{value}</strong> {unitLabel}
      </output>
      <button
        type="button"
        aria-label="Augmenter la quantité"
        onClick={() => onChange(value + step)}
      >
        <FiPlus aria-hidden="true" />
      </button>
    </div>
  );
}

export function SummaryBar({
  label,
  total,
  buttonLabel,
  disabled,
  onContinue,
}) {
  return (
    <div className="options-summary" role="region" aria-label="Récapitulatif">
      <div className="container options-summary-inner">
        <div className="options-summary-text">
          <span className="options-summary-label">{label}</span>
          <strong className="options-summary-total">{total}</strong>
        </div>
        <button
          type="button"
          className="options-continue"
          onClick={onContinue}
          disabled={disabled}
        >
          {buttonLabel}
          {!disabled && <FiArrowRight aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

export const showError = (Swal, message) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon: "error", title: "Échec", text: message });
